<template>
    <div class="h-screen bg-gray-100 flex flex-col select-none overflow-hidden" @contextmenu="preventContextMenu">
        <!-- Vendor-restricted (DRM) reader: fetched into memory and rendered in-app,
             never offered as a download. h-screen (not min-h-screen) gives the viewer
             a definite height so epub.js can size its iframe. -->
        <div class="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary text-white">
            <a href="/my-library"
                class="font-marketing font-semibold inline-flex items-center gap-1 shrink-0 hover:text-green-200">
                ← Library
            </a>
            <div class="flex items-center gap-3 min-w-0 flex-1 justify-center">
                <img v-if="meta.cover" :src="meta.cover" :alt="meta.title"
                    class="h-9 w-6 object-cover rounded shadow shrink-0" />
                <div class="min-w-0 text-center sm:text-left">
                    <div class="font-marketing font-semibold leading-tight truncate" :title="meta.title">
                        {{ meta.title || 'Reading' }}
                    </div>
                    <div class="text-xs opacity-80 truncate">
                        {{ mode === 'sample' ? 'Free sample' : (meta.author || 'Your copy') }}
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
                <button v-if="kind === 'epub'" @click="prevPage" class="px-3 py-1 rounded bg-white/10 hover:bg-white/20" aria-label="Previous page">‹</button>
                <span v-if="totalPages > 0" class="text-xs opacity-90 tabular-nums whitespace-nowrap" aria-live="polite">
                    Page {{ currentPage }} / {{ totalPages }}
                </span>
                <button v-if="kind === 'epub'" @click="nextPage" class="px-3 py-1 rounded bg-white/10 hover:bg-white/20" aria-label="Next page">›</button>
            </div>
        </div>

        <!-- Render area. The `viewer` is a STATIC element: Vue renders it once and
             never re-patches it, so epub.js/pdf.js can own its DOM without Vue's
             diff hitting the mutated subtree. Loading/error are overlays on top. -->
        <div class="relative flex-1 min-h-0">
            <div ref="viewer" class="absolute inset-0 overflow-auto bg-white"></div>
            <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-gray-100">
                <Loader />
            </div>
            <div v-else-if="error" class="absolute inset-0 flex items-center justify-center bg-gray-100 p-8">
                <div class="rounded-md border border-red-200 bg-red-50 p-4 text-red-700 font-sans max-w-md text-center">
                    {{ error }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Loader from './Loader.vue'
import { createAxiosInstance } from '../utils/axios.js'
import { userService } from '../services/user'

const props = defineProps({
    id: { type: [String, Number], default: null },
    mode: { type: String, default: 'purchase' } // 'purchase' | 'sample'
})

const api = createAxiosInstance()
const viewer = ref(null)
const loading = ref(true)
const error = ref('')
const kind = ref('') // 'epub' | 'pdf' | 'unsupported'
const meta = ref({ title: '', author: '', cover: '', fileFormat: '' })
// Page indicator (shown in the toolbar) — the page the reader is currently on
// and the total, so the customer can see and resume their place.
const currentPage = ref(0)
const totalPages = ref(0)
let rendition = null
let book = null // epub.js Book — kept for location-based page numbering
let pdfCanvases = [] // rendered <canvas> per PDF page, for scroll→page mapping

const contentUrl = () =>
    props.mode === 'sample'
        ? userService.sampleContentUrl(props.id)
        : userService.contentUrl(props.id)

// Block print/save keyboard shortcuts while reading.
const blockShortcuts = (e) => {
    const k = (e.key || '').toLowerCase()
    if ((e.ctrlKey || e.metaKey) && (k === 'p' || k === 's')) {
        e.preventDefault()
    }
}

const prevPage = () => rendition && rendition.prev()
const nextPage = () => rendition && rendition.next()
const preventContextMenu = (e) => e.preventDefault()

// Two rAFs ensures the browser has laid out the (now visible) viewer so
// renderers measure a real height instead of 0.
const nextFrame = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))

const detectKind = (contentType, fileFormat) => {
    const ct = (contentType || '').toLowerCase()
    const ff = (fileFormat || '').toLowerCase()
    if (ct.includes('pdf') || ff === 'pdf') return 'pdf'
    if (ct.includes('epub') || ff === 'epub') return 'epub'
    return 'unsupported'
}

// --- Resume progress (purchased items only; samples don't track) ---
const isResumable = () => props.mode !== 'sample' && !!props.id
let currentPos = null
let lastSave = 0
const saveNow = () => {
    if (isResumable() && currentPos != null && currentPos !== '') {
        userService.saveProgress(props.id, currentPos)
    }
}
const throttledSave = () => {
    const now = Date.now()
    if (now - lastSave > 5000) { lastSave = now; saveNow() }
}
// Which PDF page is centered in the viewport right now (1-based).
const updatePdfPage = () => {
    if (!viewer.value || pdfCanvases.length === 0) return
    const mid = viewer.value.scrollTop + viewer.value.clientHeight / 2
    let page = 1
    for (let i = 0; i < pdfCanvases.length; i++) {
        if (pdfCanvases[i].offsetTop <= mid) page = i + 1
        else break
    }
    currentPage.value = page
}

const onPdfScroll = () => {
    if (!viewer.value) return
    currentPos = String(viewer.value.scrollTop)
    updatePdfPage()
    throttledSave()
}

const renderEpub = async (buffer, startCfi) => {
    const epubMod = await import('epubjs')
    const ePub = epubMod.default || epubMod
    book = ePub(buffer)
    await book.ready
    rendition = book.renderTo(viewer.value, {
        width: '100%',
        height: '100%',
        flow: 'paginated',
        allowScriptedContent: true
    })
    await rendition.display(startCfi || undefined)
    rendition.on('relocated', (loc) => {
        currentPos = loc?.start?.cfi || currentPos
        if (book?.locations && totalPages.value > 0 && loc?.start?.cfi) {
            const idx = book.locations.locationFromCfi(loc.start.cfi)
            if (typeof idx === 'number' && idx >= 0) currentPage.value = idx + 1
        }
        throttledSave()
    })
    // Build a location map so we can show a stable "page X of Y". This is async
    // and can lag on large books; the indicator simply appears once it's ready.
    book.locations.generate(1024).then(() => {
        totalPages.value = book.locations.length()
        const cfi = currentPos || startCfi
        if (cfi) {
            const idx = book.locations.locationFromCfi(cfi)
            if (typeof idx === 'number' && idx >= 0) currentPage.value = idx + 1
        }
    }).catch(() => { /* page numbering is best-effort */ })
}

const renderPdf = async (buffer) => {
    const pdfjsLib = await import('pdfjs-dist')
    // Worker matched to the installed pdf.js version (string form keeps it
    // parseable under babel-jest).
    if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc =
            `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
    }
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
    // Center pages in a JS-created wrapper (the viewer is Vue-static, so we build
    // its contents imperatively).
    const wrap = document.createElement('div')
    wrap.className = 'flex flex-col items-center gap-4 py-4 bg-gray-100 min-h-full'
    viewer.value.appendChild(wrap)
    pdfCanvases = []
    totalPages.value = pdf.numPages
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const viewport = page.getViewport({ scale: 1.3 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.className = 'shadow-md bg-white max-w-full'
        wrap.appendChild(canvas)
        pdfCanvases.push(canvas)
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
    }
}

const loadMeta = async () => {
    const m = props.mode === 'sample'
        ? await userService.getSampleMeta(props.id)
        : await userService.getPurchaseMeta(props.id)
    if (m) meta.value = { ...meta.value, ...m }
}

const load = async () => {
    loading.value = true
    error.value = ''
    try {
        const response = await api.get(contentUrl(), { responseType: 'blob' })
        const blob = response.data
        const contentType = (blob.type || response.headers?.['content-type'] || '').toLowerCase()
        kind.value = detectKind(contentType, meta.value.fileFormat)
        if (kind.value === 'unsupported') {
            loading.value = false
            error.value = 'This format can’t be read in the browser yet (supported: PDF and EPUB).'
            return
        }
        const buffer = await blob.arrayBuffer()
        const saved = isResumable() ? await userService.getProgress(props.id) : null
        loading.value = false
        await nextFrame() // viewer now visible; let it lay out before rendering
        if (kind.value === 'pdf') {
            await renderPdf(buffer)
            if (saved != null && viewer.value) {
                viewer.value.scrollTop = parseFloat(saved) || 0
            }
            updatePdfPage() // reflect the restored position in the indicator
            viewer.value?.addEventListener('scroll', onPdfScroll, { passive: true })
        } else {
            await renderEpub(buffer, saved)
        }
    } catch (e) {
        console.error('EbookReader failed to open title:', e)
        loading.value = false
        error.value =
            e.response?.status === 404
                ? 'This title is not available to read.'
                : 'Could not open this title. Please try again.'
    }
}

onMounted(() => {
    window.addEventListener('keydown', blockShortcuts)
    loadMeta()
    load()
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', blockShortcuts)
    viewer.value?.removeEventListener('scroll', onPdfScroll)
    saveNow() // persist final position on leave
    if (rendition && typeof rendition.destroy === 'function') {
        rendition.destroy()
    }
    if (book && typeof book.destroy === 'function') {
        book.destroy()
    }
})
</script>
